import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
//local
import {ClosableTags} from "components/ClosableTags";
import {EmailSuggestCompanyModal} from "components/modals/EmailSuggestCompanyModal";
import {
  IconHandOnRight,
  IconMailLetter,
  IconFunnel,
  IconSkullEmpty,
  IconUE2021,
  IconRamavtal,
} from "components/icons";
import {color} from "constants/color.consts";
import ButtonDelete from "components/buttons/ButtonDelete";
import EmailRecipientsBouncedStatus from "../EmailRecipientsBouncedStatus";
import {InitialsBadge} from "components/InitialsBadge";
import {CustomCounter} from "components/CustomCounter";
import {MarkingIcons} from "components/MarkingIcons";
import {getFullName, hasNoLegacySupplierQualification} from "helpers/helper";
import {ourAgreementsTypes} from "constants/user.consts";

// type: "filter" | "organization" | "email"
export const RecipientsEditRow = ({
  type,
  organization,
  currentUser,
  dontRemove,
  isPublishedRequest,
  markings,
  remove,
  onTagClick,
  isFavorite,
  isBlacklisted,
  isSuggestModal, // for email type row
  isShowInfoLabel, // for organization, if modal did not open
  selectedModalOrganization, // for email type row
  onConvertEmailToOrganization, // for email type row
  closeSuggestModal, // for email type row
}) => {
  const {t} = useTranslation();

  const typeColor = {
    filter: "link",
    organization: "green",
    email: "purple",
  };

  const ourAgreements =
    organization.Organization?.OurAgreements ?? organization?.OurAgreements;
  const isUE2021 = !!ourAgreements?.find(
    (item) => item?.type === ourAgreementsTypes.UE2021
  );
  const isRamavtal = !!ourAgreements?.find(
    (item) => item?.type === ourAgreementsTypes.RAMAVTAL
  );

  const membersToReceiveIds =
    organization.MembersToReceive?.map((member) => member.id) ?? [];

  const members = membersToReceiveIds.length
    ? organization.Organization?.Users?.filter((member) =>
        membersToReceiveIds.includes(member.id)
      )
    : organization.Organization?.Users?.filter(
        (member) => !!member.OrganizationMember?.isRfpEmailNotifications
      );

  const isFilterType = useMemo(() => type === "filter", [type]);
  const isOrganizationType = useMemo(() => type === "organization", [type]);
  const isEmailType = useMemo(() => type === "email", [type]);

  const disableRemove = (tag) =>
    tag instanceof Object
      ? !dontRemove?.some((item) => item.id === tag.id)
      : !dontRemove?.includes(tag);

  const handleAddEmail = () => {
    onConvertEmailToOrganization(selectedModalOrganization);
    closeSuggestModal();
  };

  return (
    <div className="recipients-tab_table-row_wrapper">
      {isFilterType && (
        <IconFunnel width="20" color={color[typeColor[type]]} className="mr-4" />
      )}
      {isOrganizationType && (
        <IconHandOnRight width="20" color={color[typeColor[type]]} className="mr-4" />
      )}
      {isEmailType && (
        <IconMailLetter
          width="20"
          height="13"
          color={color[typeColor[type]]}
          className="mr-4"
        />
      )}
      <div
        className="recipients-tab_table-row_organization"
        onClick={() => (isOrganizationType ? onTagClick(organization) : null)}
      >
        <ClosableTags
          tags={[
            isEmailType
              ? organization
              : {
                  ...organization,
                  fullName: organization?.name || "",
                },
          ]}
          field={isEmailType ? "email" : "fullName"}
          index={isEmailType ? "email" : "id"}
          small
          tagClass="recipients-tab_table-row_organization_tag"
          color={typeColor[type]}
          dontRemove={false}
          isRelativeCustomContent
          customContent={
            isEmailType &&
            isSuggestModal && (
              <EmailSuggestCompanyModal
                organization={selectedModalOrganization}
                onClose={closeSuggestModal}
                onAdd={handleAddEmail}
              />
            )
          }
        />
        {hasNoLegacySupplierQualification(
          true,
          isFavorite,
          !!organization.Organization?.SupplierQualifications?.length,
          currentUser?.ActiveOrganization?.enableSuppliersQualification
        )}
        {isBlacklisted && <IconSkullEmpty height={20} width={20} />}
        {isUE2021 && <IconUE2021 />}
        <MarkingIcons
          organization={organization.Organization}
          markings={markings}
          addCounterClass="mr-1"
        />
        {isRamavtal && <IconRamavtal />}
        {!!members?.length &&
          members.slice(0, 3).map((member, i) => (
            <React.Fragment key={member.id || `member-${i}`}>
              <InitialsBadge
                key={member.id || i}
                firstName={member.firstName}
                lastName={member.lastName}
                addClass="mr-1"
              />
              <span className="fz-12 mr-2">
                {getFullName(member.firstName, member.lastName)}
              </span>
            </React.Fragment>
          ))}
        {!!members?.length && members.length > 3 && (
          <CustomCounter
            count={members.length - 3}
            extraText={t("OTHERS")}
            small
            addClass="ml-0 mr-2"
          />
        )}
        {isOrganizationType && isShowInfoLabel && (
          <span className="recipients-tab_table-row_organization_info">
            {t("RECIPIENTS_REGISTERED_OPEN_MODAL")}
          </span>
        )}
        {isEmailType && organization.isBounced && <EmailRecipientsBouncedStatus />}
      </div>
      {(!isPublishedRequest || disableRemove(organization)) && (
        <ButtonDelete
          activeOnHover
          onClick={() => remove(isEmailType ? organization : organization.id)}
        />
      )}
    </div>
  );
};
